import { AspectRatio } from 'components/KalturaVideo/Styled';
import { Button } from '@teachstonellc/component-library';
import DimensionTagLabel from 'components/LearningResources/DimensionTagLabel';
import { getHoursMinutesSeconds } from '../utils';
import {
  getPlaylistItems,
  startPlaylistTrack,
  completePlaylistTrack
} from 'actions/learningResources';
import { getConfiguration, getDomId } from 'pages/Testing/Reliability/utils';
import i18n from 'helpers/i18n';
import { LearningTracks, PlaylistRecord } from 'types/LearningResources/types';
import MarkAsImportant from 'components/LearningResources/MarkAsImportant';
import messages from '../DetailPage/messages';
import Playlist from 'components/LearningResources/Playlist/Playlist';
import ProgressBar from 'components/LearningResources/Playlist/ProgressBar';
import RichTextEditor from 'components/RichTextEditor';
import RouteHelpers from 'helpers/routes';
import SupportingAssets from 'components/LearningResources/SupportingAssets';
import { Tag } from 'types/api/Tag';
import { useEffect, useState } from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import { useRouteParams } from 'hooks/useRouteParams';
import { USER_ENGAGEMENT_EVENT_VERBS } from 'constants/userEngagementEventVerbs';
import useUserEngagementEvent from 'hooks/useUserEngagementEvent';
import { VideoPlayerContainer } from 'components/ReliabilityTest/CodingVideos/Styled';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from '@teachstonellc/component-library/dist/icons';
import { getCurrentLearningResource } from './utils';

type ParamsType = {
  id: string;
};

export type PlaylistCurrentTrack = {
  current_track_id: number;
};

// TO DO: move to util when BE json is finalized
const findDimensionTag = (tags?: Tag[]): Tag | undefined => {
  let dimensionTag = tags?.find(t => t.group?.name === 'dimension');
  return dimensionTag;
};

function PlaylistDetailPage() {
  const [playlist, setPlaylist] = useState<
    PlaylistRecord & PlaylistCurrentTrack
  >();
  const [playbackHasBegun, setPlaybackHasBegun] = useState(false);
  const [playbackHasEnded, setPlaybackHasEnded] = useState(false);
  const [currentLearningResource, setCurrentLearningResource] =
    useState<LearningTracks>();
  const { id: playListID } = useRouteParams<ParamsType>();
  const { id: userId } = useCurrentUser();
  const navigate = useNavigate();
  const isIntro = currentLearningResource?.type === 'intro';
  const isVideoResource =
    currentLearningResource?.learning_resources.resource_type === 'video';
  const isNonVideoResource =
    currentLearningResource?.learning_resources.resource_type === 'non_video';

  const kalturaPlayerID = getDomId(`${playListID ? playListID : 1}`);

  const buildIntroTrack = (notes: string): LearningTracks => ({
    id: -1,
    completed_at: '',
    current: false,
    type: 'intro',
    learning_resources: {
      id: -1,
      name: 'Introduction',
      estimated_time: 0,
      content: notes,
      notes: '',
      video_uri: '',
      resource_type: ''
    }
  });

  /* when video playback is initiated, transmit the user event */
  useUserEngagementEvent({
    objectId: currentLearningResource?.learning_resources.id,
    objectType: 'LearningResourceRecord',
    userId,
    verb: USER_ENGAGEMENT_EVENT_VERBS.BEGINS_PLAYBACK,
    ready: playbackHasBegun && !isIntro
  });

  /* when video is watched to the end, transmit the user event */
  useUserEngagementEvent({
    objectId: currentLearningResource?.learning_resources.id,
    objectType: 'LearningResourceRecord',
    userId,
    verb: USER_ENGAGEMENT_EVENT_VERBS.COMPLETES,
    ready: playbackHasEnded && !isIntro
  });

  /* load data */
  useEffect(() => {
    if (!playListID) {
      return;
    }

    getPlaylistItems(parseInt(playListID))
      .then(response => {
        const data = response.data;
        const introTrack = buildIntroTrack(data.notes);

        const updatedData = {
          ...data,
          learning_tracks: [introTrack, ...data.learning_tracks]
        };

        setPlaylist(updatedData);

        let initialTrack: LearningTracks = introTrack;
        if (data.started) {
          initialTrack =
            data.current_track_id == null
              ? updatedData.learning_tracks[
                  updatedData.learning_tracks.length - 1
                ]
              : getCurrentLearningResource(updatedData);
        }
        setCurrentLearningResource(initialTrack);
      })
      .catch(() => {});
  }, [playListID]);

  /* set up the video player */
  useEffect(() => {
    if (!currentLearningResource || isIntro || isNonVideoResource) {
      return;
    }

    try {
      /* configure a video player */
      const kalturaPlayer = (window as any).KalturaPlayer?.setup(
        getConfiguration(kalturaPlayerID)
      );

      if (!kalturaPlayer) {
        return;
      }

      /* load the video */
      kalturaPlayer.loadMedia({
        entryId: currentLearningResource.learning_resources.video_uri
      });

      /* listen for the first play event and log it */
      kalturaPlayer.addEventListener(
        kalturaPlayer.Event.Core.FIRST_PLAYING,
        (_e: unknown) => {
          setPlaybackHasBegun(true);
        }
      );

      // CALL createFirstViewsEvent (from utils.ts) here

      /* listen for the play back ended event and log it */
      kalturaPlayer.addEventListener(
        kalturaPlayer.Event.Core.PLAYBACK_ENDED,
        (_e: unknown) => {
          setPlaybackHasEnded(true);
        }
      );
    } catch {
      return;
    }

    /* clean up */
    return () => {
      try {
        const kalturaPlayer = (window as any).KalturaPlayer?.getPlayer(
          kalturaPlayerID
        );
        kalturaPlayer?.destroy();
      } catch {
        return;
      }
    };
  }, [
    kalturaPlayerID,
    currentLearningResource,
    userId,
    isIntro,
    isNonVideoResource
  ]);

  useEffect(() => {
    if (isIntro || (isVideoResource && !playbackHasEnded)) {
      return;
    }

    if (currentLearningResource) {
      completePlaylistTrack(parseInt(playListID), currentLearningResource.id)
        .then(response => {
          const data = response.data;
          const introTrack = buildIntroTrack(data.notes);

          const updatedData = {
            ...data,
            learning_tracks: [introTrack, ...data.learning_tracks]
          };

          setPlaylist(updatedData);
          if (!isNonVideoResource) {
            // Always go to the next resource in sequence, regardless of completion
            const currentIdx: number = updatedData.learning_tracks.findIndex(
              (t: LearningTracks) => t.id === currentLearningResource.id
            );
            const nextTrack = updatedData.learning_tracks[currentIdx + 1];
            if (nextTrack) {
              setCurrentLearningResource(nextTrack);
            }
          }
          setPlaybackHasEnded(false);
        })
        .catch(() => {});
    }
  }, [
    currentLearningResource,
    isIntro,
    isNonVideoResource,
    isVideoResource,
    playListID,
    playbackHasEnded
  ]);

  useEffect(() => {
    if (isVideoResource && !playbackHasBegun) {
      return;
    }

    startPlaylistTrack(parseInt(playListID))
      .then(response => {
        setPlaybackHasBegun(false);
      })
      .catch(() => {});
  }, [isVideoResource, playListID, playbackHasBegun]);

  function isNextButtonDisabled(
    playlist: PlaylistRecord & PlaylistCurrentTrack,
    currentLearningResource: LearningTracks
  ): boolean {
    const idx = playlist.learning_tracks.findIndex(
      t => t.id === currentLearningResource.id
    );
    const isIntro = idx === 0;
    if (isIntro) return false;
    const isLast = idx === playlist.learning_tracks.length - 1;
    const currentCompleted = playlist.learning_tracks[idx]?.completed_at;
    return isLast || !currentCompleted;
  }

  function onClickNext(
    playlist: PlaylistRecord & PlaylistCurrentTrack,
    currentLearningResource: LearningTracks
  ) {
    const currentIdx = playlist.learning_tracks.findIndex(
      t => t.id === currentLearningResource.id
    );
    const isIntro = currentIdx === 0;
    if (isIntro) {
      setCurrentLearningResource(playlist.learning_tracks[1]);
      return;
    }
    const isLast = currentIdx === playlist.learning_tracks.length - 1;
    const isCompleted = playlist.learning_tracks[currentIdx]?.completed_at;

    if (isLast || !isCompleted) {
      return;
    }

    const nextTrack = playlist.learning_tracks[currentIdx + 1];
    if (nextTrack) {
      setCurrentLearningResource(nextTrack);
    }
  }

  function onClickBack(
    playlist: PlaylistRecord & PlaylistCurrentTrack,
    currentLearningResource: LearningTracks
  ) {
    const currentIdx = playlist.learning_tracks.findIndex(
      t => t.id === currentLearningResource.id
    );
    if (currentIdx > 0) {
      setCurrentLearningResource(playlist.learning_tracks[currentIdx - 1]);
    }
  }

  if (!playlist || !currentLearningResource) {
    return <></>;
  }

  const fallbackImage =
    'https://dashboard.teachstone.com/static/media/teachstone-bg.19a12ea37554c05f9ffb.jpg';

  return (
    <>
      <div className="my-4 flex font-bold text-blue-500">
        <Link
          to="#"
          onClick={e => {
            e.preventDefault();
            navigate(-1);
          }}
          className="inline-flex items-center gap-1"
        >
          <ChevronLeft />
          {i18n.ft(messages.back)}
        </Link>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-8 bg-[#F5F5F5]">
          <div className="">
            <div className="flex justify-center mt-4">
              {isIntro && (
                <img
                  src={playlist.intro_url || fallbackImage}
                  alt="Playlist Intro"
                  className="object-fill w-full max-h-[505px]"
                />
              )}
              {!isIntro && isNonVideoResource && (
                <img
                  src={
                    currentLearningResource.learning_resources.thumbnail_url ||
                    fallbackImage
                  }
                  alt="Resource Thumbnail"
                  className="object-fill w-full max-h-[505px]"
                />
              )}
              <AspectRatio />
              <VideoPlayerContainer
                id={kalturaPlayerID}
                isTablet={false}
                style={{
                  display:
                    isIntro ||
                    currentLearningResource.learning_resources.resource_type ===
                      'non_video'
                      ? 'none'
                      : 'block'
                }}
              />
            </div>
            <div className="mt-4 mx-4 md:mt-0 md:mx-0">
              <div className="bg-white p-4 pb-8">
                {!isIntro && (
                  <div className="text-base">
                    <span className="font-bold text-black">
                      {i18n.ft(messages.time)}
                    </span>
                    <span className="pl-4">
                      {getHoursMinutesSeconds(
                        currentLearningResource.learning_resources
                          .estimated_time
                      )}
                    </span>
                  </div>
                )}

                {!isIntro && (
                  <MarkAsImportant
                    learningResourceID={
                      currentLearningResource.learning_resources.id
                    }
                    userId={userId}
                  />
                )}

                <DimensionTagLabel tag={findDimensionTag(playlist.tags)} />
              </div>

              <div className="bg-white p-4 pb-6 mt-6 md:mt-0">
                <h2 className="font-bold text-3xl mb-4">
                  {currentLearningResource.learning_resources.name}
                </h2>
                {currentLearningResource.learning_resources.content && (
                  <div className="text-black">
                    <RichTextEditor
                      headings
                      readOnly
                      value={currentLearningResource.learning_resources.content}
                    />
                  </div>
                )}

                {playlist.assets ? (
                  <SupportingAssets assets={playlist.assets} />
                ) : (
                  <></>
                )}
              </div>
              {/* Back/Next Buttons */}
              <div className="flex justify-between items-center text-[#505050]">
                {/* Back Button */}
                {playlist.learning_tracks.findIndex(
                  t => t.id === currentLearningResource.id
                ) !== 0 ? (
                  <Button
                    variant="ghost"
                    className="font-medium"
                    onClick={() =>
                      onClickBack(playlist, currentLearningResource)
                    }
                    startIcon={'fa-solid fa-arrow-left'}
                  >
                    Previous
                  </Button>
                ) : (
                  <div />
                )}
                {/* Next Button */}
                {(() => {
                  const idx = playlist.learning_tracks.findIndex(
                    t => t.id === currentLearningResource.id
                  );
                  const isLast = idx === playlist.learning_tracks.length - 1;
                  if (isLast) {
                    return (
                      <Button
                        className="font-medium"
                        variant="ghost"
                        onClick={() =>
                          navigate(RouteHelpers.getPath('learning-resources'))
                        }
                        endIcon={'fa-solid fa-arrow-right'}
                      >
                        {i18n.ft(messages.home)}
                      </Button>
                    );
                  }
                  return (
                    <Button
                      className="font-medium"
                      variant="ghost"
                      disabled={isNextButtonDisabled(
                        playlist,
                        currentLearningResource
                      )}
                      onClick={() =>
                        onClickNext(playlist, currentLearningResource)
                      }
                      endIcon={
                        isNextButtonDisabled(playlist, currentLearningResource)
                          ? 'fa-solid fa-lock'
                          : 'fa-solid fa-arrow-right'
                      }
                    >
                      Next
                    </Button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 xl:col-span-4">
          <div className="mt-4 mx-4 md:mt-0 md:mx-0">
            <div className="mt-4 pb-4 text-xl font-bold">{playlist.name}</div>
            <div className="flex items-center gap-3 text-lg">
              <span>{i18n.ft(messages.playlist.subtitle)}</span>
              <span
                style={{
                  width: '4px',
                  height: '4px',
                  backgroundColor: '#94A3B8',
                  borderRadius: '50%',
                  display: 'inline-block'
                }}
              />
              <span>
                {i18n.ft(messages.playlist.resourcesCount, {
                  count: playlist.learning_tracks.length - 1
                })}
              </span>
            </div>
            <ProgressBar percentage={playlist.completion_percentage} />

            <Playlist
              playlist={playlist}
              currentVideoId={currentLearningResource.learning_resources.id}
              setCurrentLearningResource={setCurrentLearningResource}
              isIntro={isIntro}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default PlaylistDetailPage;
